"use client";

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { providerApi } from '@/api';
import { Provider } from '@/store';
import { ProviderCard } from '@/components/provider/ProviderCard';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  Loader2, 
  MapPin, 
  Briefcase, 
  Star, 
  Mic, 
  X,
  Zap,
  ThumbsUp,
  SlidersHorizontal,
  LocateFixed,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

// --- 1. HARDCODED LISTS ---

const INDIA_CITIES = [
  // Bihar
  "Patna","Gaya","Bhagalpur","Muzaffarpur","Purnia","Darbhanga","Begusarai",
  "Katihar","Ara","Buxar","Samastipur","Motihari","Sasaram","Siwan","Chhapra",

  // Delhi
  "Delhi","New Delhi","Dwarka","Rohini","Saket","Karol Bagh",
   // Maharashtra
  "Mumbai","Pune","Nagpur","Nashik","Aurangabad","Solapur","Kolhapur",
  "Amravati","Thane","Navi Mumbai","Kalyan","Dombivli",

  // West Bengal
  "Kolkata","Howrah","Durgapur","Asansol","Siliguri","Malda","Kharagpur",

  // Karnataka
  "Bangalore","Mysore","Mangalore","Hubli","Dharwad","Belgaum",
  "Shimoga","Tumkur","Udupi",

  // Telangana
  "Hyderabad","Warangal","Karimnagar","Nizamabad","Khammam",

  // Tamil Nadu
  "Chennai","Coimbatore","Madurai","Tiruchirappalli","Salem",
  "Erode","Vellore","Tirunelveli","Thoothukudi",

   // Uttar Pradesh
  "Lucknow","Kanpur","Varanasi","Prayagraj","Agra","Meerut","Ghaziabad",
  "Noida","Greater Noida","Aligarh","Bareilly","Moradabad","Jhansi",

  // Rajasthan
  "Jaipur","Jodhpur","Udaipur","Kota","Ajmer","Bikaner","Alwar","Bharatpur",

   // Madhya Pradesh
  "Indore","Bhopal","Gwalior","Jabalpur","Ujjain","Sagar","Rewa",

   // Gujarat
  "Ahmedabad","Surat","Vadodara","Rajkot","Bhavnagar","Jamnagar","Junagadh",

   // Haryana
  "Gurgaon","Faridabad","Panipat","Ambala","Hisar","Rohtak","Karnal",

   // Punjab
  "Chandigarh","Ludhiana","Amritsar","Jalandhar","Patiala","Bathinda",

   // Jharkhand
  "Ranchi","Jamshedpur","Dhanbad","Bokaro","Hazaribagh","Deoghar",

   // Odisha
  "Bhubaneswar","Cuttack","Rourkela","Sambalpur","Puri","Balasore",

   // Chhattisgarh
  "Raipur","Bhilai","Bilaspur","Korba","Durg","Rajnandgaon",

   // Kerala
  "Thiruvananthapuram","Kochi","Kozhikode","Thrissur","Kollam","Alappuzha",

   // Andhra Pradesh
  "Visakhapatnam","Vijayawada","Guntur","Nellore","Tirupati","Kurnool",

   // Assam
  "Guwahati","Silchar","Dibrugarh","Jorhat","Tezpur",

  // Himachal Pradesh
  "Shimla","Solan","Mandi","Dharamshala","Una",

  // Uttarakhand
  "Dehradun","Haridwar","Roorkee","Haldwani","Nainital",

  // J&K
  "Srinagar","Jammu","Anantnag","Baramulla",

   // North-East
  "Imphal","Aizawl","Agartala","Shillong","Itanagar","Kohima",

 // Goa
  "Panaji","Margao","Vasco da Gama"
].sort();

const STATIC_CATEGORIES = [
  "Plumber", "Electrician", "Carpenter", "Painter", "AC Repair", "Appliance Repair", 
  "Mechanic", "Cleaner", "Pest Control", "Teacher", "Music Teacher", "Photographer", 
  "Event Planner", "Graphic Designer", "Software Developer", "Makeup Artist", "Salon", 
  "Driver", "Cook", "Interior Designer", "Packers & Movers", "Other",
       "Home Deep Cleaning", "Bathroom Cleaning", "Kitchen Cleaning", "Water Tank Cleaning",
  "RO Water Purifier Service", "Geyser Repair", "Washing Machine Repair",
  "Refrigerator Repair", "Microwave Repair", "TV Repair", "Laptop Repair",
  "Mobile Repair", "CCTV Installation", "Inverter Repair", "Solar Panel Installation",
  "Gas Stove Repair", "Chimney Repair", "Furniture Assembly", "Modular Kitchen Installer",
  "False Ceiling Worker", "Tile Fitter", "Marble Polishing", "Welding Work",
  "Fabrication Worker", "Glass & Aluminum Work",

   // Construction & Labour
  "Mason (Raj Mistri)", "Construction Labour", "Civil Helper",
  "Scaffolding Worker", "Road Construction Worker",
  "Plaster Worker", "Shuttering Carpenter",

   // Personal Services
  "House Maid", "Babysitter", "Nanny", "Elder Care Assistant",
  "Patient Care Attendant", "Caretaker", "Home Nurse",
  "Physiotherapist", "Yoga Instructor", "Fitness Trainer",
  "Zumba Trainer", "Dietitian", "Nutritionist",

   // Beauty & Grooming
  "Hair Stylist", "Barber", "Mehndi Artist", "Nail Artist",
  "Spa Therapist", "Massage Therapist", "Bridal Makeup Artist",
  "Skin Care Specialist",

   // Education & Training
  "Home Tutor", "Online Tutor", "Maths Teacher", "Science Teacher",
  "English Speaking Trainer", "Computer Teacher",
  "Coding Instructor", "Dance Teacher", "Art Teacher",
  "Calligraphy Teacher", "Abacus Trainer",
  "Competitive Exam Coach",

   // IT & Digital
  "Web Developer", "Frontend Developer", "Backend Developer",
  "Full Stack Developer", "Mobile App Developer",
  "WordPress Developer", "Shopify Developer",
  "SEO Expert", "Digital Marketing Executive",
  "Social Media Manager", "Content Writer",
  "Copywriter", "Technical Writer",
  "Video Editor", "Motion Graphics Designer",
  "UI/UX Designer", "Data Analyst", "Data Entry Operator",
  "Virtual Assistant", "AI Prompt Engineer",

   // Media & Creative
  "Videographer", "Cinematographer",
  "YouTube Video Editor", "Reel Editor",
  "Voice Over Artist", "Anchor", "Podcast Editor",
  "Script Writer", "Storyboard Artist",

   // Transport & Logistics
  "Cab Driver", "Auto Driver", "Truck Driver",
  "Delivery Boy", "Bike Delivery Partner",
  "Courier Service", "Tempo Service",
  "Logistics Helper", "Warehouse Worker",
  "Fleet Manager",

   // Event & Hospitality
  "Caterer", "Wedding Planner", "Event Decorator",
  "Stage Decorator", "DJ", "Sound System Operator",
  "Light Technician", "Event Helper",
  "Waiter", "Bartender",

   // Food Services
  "Home Chef", "Tiffin Service Provider",
  "Cloud Kitchen Staff", "Baker",
  "Cake Designer", "Sweet Maker",
  "Fast Food Vendor", "Street Food Vendor",

   // Business & Office Support
  "Accountant", "Bookkeeper", "Tax Consultant",
  "GST Consultant", "Company Secretary Assistant",
  "HR Recruiter", "Payroll Executive",
  "Office Assistant", "Back Office Executive",

   // Sales & Marketing
  "Field Sales Executive", "Telecaller",
  "Customer Support Executive",
  "Business Development Executive",
  "Brand Promoter", "Survey Enumerator",

   // Repair & Technical
  "Printer Repair", "Biometric Device Technician",
  "POS Machine Technician", "ATM Technician",
  "Lift Technician", "Escalator Technician",

   // Agriculture & Rural
  "Farm Labour", "Gardener", "Landscaper",
  "Nursery Worker", "Dairy Worker",
  "Poultry Farm Worker", "Organic Farming Consultant",

   // Legal & Professional
  "Legal Assistant", "Documentation Executive",
  "Notary Assistant", "Property Consultant",
  "Real Estate Agent",

   // Security & Safety
  "Security Guard", "Bouncer",
  "Fire Safety Technician",
  "CCTV Monitoring Staff",

   // Miscellaneous
  "Astrologer", "Pandit", "Priest",
  "Puja Service Provider",
  "Vastu Consultant", "Numerologist",
  "Tour Guide", "Travel Agent",
  "Ticket Booking Agent",
  "Laundry Service", "Dry Cleaner",
  "Ironing Service"
].sort();

const RATINGS = [
  { value: 'all', label: 'All Ratings' },
  { value: 'p*', label: '⭐ Excellent (p*)' },
  { value: 'p++', label: '⭐ Very Good (p++)' },
  { value: 'p+', label: '⭐ Good (p+)' },
  { value: 'p', label: '⭐ Basic (p)' },
];

const AVAILABILITY_OPTIONS = [
  { value: 'any', label: 'Any Status' },
  { value: 'Available', label: '🟢 Available Now' },
  { value: 'Busy', label: '🔴 Busy / On Job' },
];

const ENDORSEMENT_OPTIONS = [
  { value: 'any', label: 'Any Trust Level' },
  { value: '1', label: '👍 Endorsed by 1+' },
  { value: '2', label: '👍 Endorsed by 2+' },
  { value: '3', label: '👍 Endorsed by 3+' },
  { value: '4', label: '👍 Endorsed by 4+' },
  { value: '5', label: '👍 Endorsed by 5+' },
];

function ProvidersContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  
  // Data State
  const [providers, setProviders] = useState<Provider[]>([]);
  const [categories] = useState<string[]>(STATIC_CATEGORIES); 
  
  // ✅ Pagination State
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 9; // Must match backend default or be passed

  // UI State
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [locating, setLocating] = useState(false);
  const [isListening, setIsListening] = useState(false);

  // Filters State
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || 'All Categories',
    search_query: searchParams.get('q') || '',
    city_filter: searchParams.get('city') || 'All Cities', 
    rating: searchParams.get('rating') || 'all',
    availability: searchParams.get('availability') || 'Available',
    min_trust: searchParams.get('min_trust') || 'any',
    lat: searchParams.get('lat') || '',
    lng: searchParams.get('lng') || '',
  });

  const [locationDisplay, setLocationDisplay] = useState(searchParams.get('q') || '');

  // --- EFFECTS ---
  useEffect(() => {
    // Reset to page 1 if URL params change, otherwise fetch current page
    const urlPage = parseInt(searchParams.get('page') || '1');
    setPage(urlPage);
    fetchProviders(filters, urlPage);
  }, [searchParams]); // Re-run when URL changes

  const fetchProviders = async (currentFilters = filters, currentPage = 1) => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: ITEMS_PER_PAGE
      };
      
      // A. Text Search 
      if (currentFilters.search_query) params.q = currentFilters.search_query;
      
      // B. Dropdown Filters
      if (currentFilters.category && currentFilters.category !== 'All Categories') 
          params.category = currentFilters.category;
      
      if (currentFilters.availability && currentFilters.availability !== 'any') 
          params.availability = currentFilters.availability;
      
      if (currentFilters.min_trust && currentFilters.min_trust !== 'any') 
          params.min_trust = currentFilters.min_trust;
      
      if (currentFilters.rating && currentFilters.rating !== 'all') 
          params.rating = currentFilters.rating;

      // C. Location Handling
      if (currentFilters.lat && currentFilters.lng) {
          params.lat = currentFilters.lat;
          params.lng = currentFilters.lng;
      }
      
      // D. City Filter
      if (currentFilters.city_filter && currentFilters.city_filter !== 'All Cities') {
          if (!params.q) {
             params.q = currentFilters.city_filter;
          } else if (!params.q.toLowerCase().includes(currentFilters.city_filter.toLowerCase())) {
             params.q = `${params.q} ${currentFilters.city_filter}`;
          }
      }

      const response = await providerApi.search(params);
      setProviders(response.providers || []);
      
      // ✅ Update Pagination Meta from Backend
      if (response.pagination) {
        setTotalPages(response.pagination.total_pages);
      } else {
        // Fallback if backend doesn't send pagination meta
        setTotalPages(1);
      }

      // ... (Rest of AI feedback logic remains same) ...
      // ✅ SMART UI SYNC (AI Feedback Loop)
      let filtersUpdated = false;
      const newFilters = { ...currentFilters };
      
      if (response.ai_category) {
          const aiCat = response.ai_category;
          const matchedCat = categories.find(c => c.toLowerCase() === aiCat.toLowerCase()) 
                        || categories.find(c => c.toLowerCase().includes(aiCat.toLowerCase()))
                        || categories.find(c => aiCat.toLowerCase().includes(c.toLowerCase()));
          
          if (matchedCat && matchedCat !== currentFilters.category) {
              newFilters.category = matchedCat;
              filtersUpdated = true;
          }
      }

      if (response.ai_location) {
           const matchedCity = INDIA_CITIES.find(c => c.toLowerCase() === response.ai_location!.toLowerCase());
           if (matchedCity && matchedCity !== currentFilters.city_filter) {
               newFilters.city_filter = matchedCity;
               filtersUpdated = true;
           }
      }

      if (filtersUpdated) {
          newFilters.search_query = ''; 
          setFilters(newFilters);
          setLocationDisplay(''); 
          // Do not toast here to avoid spamming on every page load
      }

    } catch (error) {
      console.error('Failed to fetch providers:', error);
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  // --- HANDLERS ---
  const updateUrl = (newFilters: any, newPage: number) => {
    const params = new URLSearchParams();
    
    if (newPage > 1) params.set('page', newPage.toString());

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value && value !== 'any' && value !== 'all' && value !== 'All Categories' && value !== 'All Cities' && value !== '') {
        params.set(key, value as string);
      }
    });

    router.push(`?${params.toString()}`);
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value };
    
    if (key === 'city_filter' && value !== 'All Cities') {
        newFilters.lat = '';
        newFilters.lng = '';
        newFilters.search_query = ''; 
        setLocationDisplay('');
    }

    setFilters(newFilters);
    setPage(1); // Reset to page 1 on filter change
    updateUrl(newFilters, 1);
    // fetchProviders will be triggered by useEffect on URL change
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    updateUrl(filters, newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTextSearch = () => {
    const newFilters = { 
        ...filters,
        category: 'All Categories', 
        city_filter: 'All Cities',
        lat: '', 
        lng: ''
    };

    if (locationDisplay && locationDisplay !== "Current Location Detected") {
        newFilters.search_query = locationDisplay;
    }

    setFilters(newFilters);
    setPage(1);
    updateUrl(newFilters, 1);
  };

  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newFilters = { 
            ...filters, 
            lat: latitude.toString(), 
            lng: longitude.toString(), 
            search_query: '', 
            city_filter: 'All Cities' 
        };
        setFilters(newFilters);
        setLocationDisplay("Current Location Detected");
        setPage(1);
        updateUrl(newFilters, 1);
        setLocating(false);
        toast.success("Location found!");
      },
      (error) => {
        console.error(error);
        toast.error("Could not access location");
        setLocating(false);
      }
    );
  };

  const clearFilters = () => {
    const emptyFilters = { 
      category: 'All Categories', 
      search_query: '', 
      city_filter: 'All Cities', 
      rating: 'all', 
      availability: 'Available',
      min_trust: 'any',
      lat: '', 
      lng: '' 
    };
    setFilters(emptyFilters);
    setLocationDisplay('');
    setPage(1);
    router.push('?'); // Clears all params
  };

  // ... (Voice search remains same) ...
  const startVoiceSearch = () => {
    // @ts-expect-error - SpeechRecognition is not in the DOM typings
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { toast.error("Browser not supported"); return; }
    
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-IN';
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    
    recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setLocationDisplay(transcript);
        
        const newFilters = { 
            ...filters, 
            search_query: transcript, 
            category: 'All Categories', 
            city_filter: 'All Cities',
            lat: '', 
            lng: '' 
        };
        
        setFilters(newFilters);
        setPage(1);
        updateUrl(newFilters, 1);
    };
    recognition.start();
  };


  return (
    <div className="min-h-screen bg-gray-50/50 py-6 sm:py-8">
      <div className="container max-w-6xl mx-auto px-3 sm:px-4">
        
        {/* HERO TEXT (Unchanged) */}
        <div className="mb-6 text-center md:text-left">
           <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
             Find Trusted Local <span className="text-primary">Experts</span>
           </h1>
           <p className="text-sm sm:text-base text-muted-foreground mt-2">
             From Plumbers to Teachers, find the right person for the job near you.
           </p>
        </div>

        {/* SEARCH & FILTER SECTION (Unchanged UI Structure) */}
        <div className="mb-6 sm:mb-8">
            <div className="flex flex-col gap-3">
                {/* --- OMNIBOX (Unchanged) --- */}
                <div className="relative">
                  <div className="relative flex items-center w-full h-12 bg-white rounded-full border border-gray-300 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary shadow-sm z-10">
                      
                      <div className="hidden xs:block pl-4 pr-2 text-gray-400">
                          <Search className="h-5 w-5" />
                      </div>

                      <input 
                          className="flex-1 h-full bg-transparent border-none outline-none text-gray-900 placeholder:text-gray-400 text-base pl-4 xs:pl-0 rounded-l-full"
                          placeholder="Search 'Plumber in Patna'..."
                          value={locationDisplay}
                          onChange={(e) => {
                              setLocationDisplay(e.target.value);
                              if(e.target.value === '') setFilters(prev => ({...prev, search_query: '', lat: '', lng: ''}));
                          }}
                          onKeyDown={(e) => e.key === 'Enter' && handleTextSearch()}
                      />

                      <div className="flex items-center gap-1 pr-1.5">
                          {locationDisplay && (
                              <button onClick={() => { setLocationDisplay(''); clearFilters(); }} className="p-2 text-gray-400 hover:text-red-500">
                                  <X className="h-4 w-4" />
                              </button>
                          )}
                          <div className="h-5 w-px bg-gray-200 mx-1 hidden md:block"></div>
                          
                          <div className="hidden md:flex items-center gap-1">
                            <button onClick={startVoiceSearch} className={`p-2 rounded-full transition-all ${isListening ? 'text-red-600 bg-red-50 animate-pulse' : 'text-gray-500 hover:text-primary hover:bg-gray-100'}`} title="Voice Search">
                                <Mic className="h-5 w-5" />
                            </button>
                            <button onClick={handleUseLocation} disabled={locating} className={`p-2 rounded-full transition-colors ${locating ? 'animate-spin text-primary' : 'text-gray-500 hover:text-primary hover:bg-gray-100'}`} title="Near Me">
                                {locating ? <Loader2 className="h-5 w-5" /> : <LocateFixed className="h-5 w-5" />}
                            </button>
                            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${showFilters ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                <SlidersHorizontal className="h-4 w-4" />
                                <span>Filters</span>
                            </button>
                          </div>

                          <Button onClick={handleTextSearch} size="icon" className="rounded-full h-9 w-9 ml-1 shrink-0">
                              <Search className="h-4 w-4" />
                          </Button>
                      </div>
                  </div>

                  {/* MOBILE TOOLBAR (Unchanged) */}
                  <div className="flex md:hidden items-center justify-between gap-2 mt-3 px-1">
                      <button onClick={startVoiceSearch} className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-lg border text-sm font-medium transition-colors ${isListening ? 'bg-red-50 border-red-200 text-red-600 animate-pulse' : 'bg-white border-gray-200 text-gray-700 active:bg-gray-50'}`}>
                        <Mic className="h-4 w-4" />
                        <span>Voice</span>
                      </button>
                      <button onClick={handleUseLocation} disabled={locating} className="flex-1 flex items-center justify-center gap-2 h-10 rounded-lg bg-white border border-gray-200 text-gray-700 text-sm font-medium active:bg-gray-50 disabled:opacity-70">
                         {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
                         <span>Near Me</span>
                      </button>
                      <button onClick={() => setShowFilters(!showFilters)} className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-lg border text-sm font-medium transition-colors ${showFilters ? 'bg-primary/10 border-primary/20 text-primary' : 'bg-white border-gray-200 text-gray-700 active:bg-gray-50'}`}>
                         <SlidersHorizontal className="h-4 w-4" />
                         <span>Filters</span>
                      </button>
                  </div>
                </div>

                {/* --- FILTERS DRAWER (Unchanged content) --- */}
                {showFilters && (
                    <div className="animate-in slide-in-from-top-2 fade-in bg-white p-4 rounded-xl border border-gray-200 shadow-sm mt-1">
                       {/* ... (Filter Inputs from previous code) ... */}
                       {/* Paste the exact same filter grid code here from previous component */}
                       <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {/* 1. Category */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
                                    <Briefcase className="h-3 w-3" /> Category
                                </label>
                                <Select value={filters.category} onValueChange={(val) => handleFilterChange('category', val)}>
                                    <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="All" /></SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        <SelectItem value="All Categories">All Categories</SelectItem>
                                        {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {/* ... (Repeat for City, Status, Trust, Rating inputs) ... */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
                                    <MapPin className="h-3 w-3" /> City
                                </label>
                                <Select value={filters.city_filter} onValueChange={(val) => handleFilterChange('city_filter', val)}>
                                    <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="All Cities" /></SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        <SelectItem value="All Cities">All Cities</SelectItem>
                                        {INDIA_CITIES.map(city => <SelectItem key={city} value={city}>{city}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            {/* Status */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
                                    <Zap className="h-3 w-3" /> Status
                                </label>
                                <Select value={filters.availability} onValueChange={(val) => handleFilterChange('availability', val)}>
                                    <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Any" /></SelectTrigger>
                                    <SelectContent>
                                        {AVAILABILITY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                             {/* Trust */}
                             <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
                                    <ThumbsUp className="h-3 w-3" /> Trust
                                </label>
                                <Select value={filters.min_trust} onValueChange={(val) => handleFilterChange('min_trust', val)}>
                                    <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Any Trust" /></SelectTrigger>
                                    <SelectContent>
                                        {ENDORSEMENT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                             {/* Rating */}
                             <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
                                    <Star className="h-3 w-3" /> Rating
                                </label>
                                <Select value={filters.rating} onValueChange={(val) => handleFilterChange('rating', val)}>
                                    <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Any" /></SelectTrigger>
                                    <SelectContent>
                                        {RATINGS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>

                        </div>
                        <div className="mt-4 flex justify-end">
                             <Button onClick={clearFilters} variant="outline" size="sm" className="w-full sm:w-auto">
                                Reset Filters
                             </Button>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Active Filter Badges (Unchanged) */}
{/* Active Filter Badges */}
            {!showFilters && (
                <div className="mt-2 flex flex-wrap gap-2">
                    
                    {/* 1. Near Me (Location) - Adds X to clear GPS */}
                    {filters.lat && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium border border-green-200">
                            <MapPin className="h-3 w-3" /> Near Me
                            <button 
                                onClick={() => {
                                    const newFilters = { ...filters, lat: '', lng: '' };
                                    setFilters(newFilters);
                                    setPage(1);
                                    updateUrl(newFilters, 1);
                                }} 
                                className="ml-1 text-green-600 hover:text-green-800 hover:bg-green-200 rounded-full p-0.5 transition-colors"
                            >
                                <X className="h-3 w-3"/>
                            </button>
                        </span>
                    )}

                    {/* 2. City Filter - Existing X */}
                    {filters.city_filter !== 'All Cities' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium border border-green-200">
                            <MapPin className="h-3 w-3" /> {filters.city_filter}
                            <button 
                                onClick={() => handleFilterChange('city_filter', 'All Cities')} 
                                className="ml-1 text-green-600 hover:text-green-800 hover:bg-green-200 rounded-full p-0.5 transition-colors"
                            >
                                <X className="h-3 w-3"/>
                            </button>
                        </span>
                    )}

                    {/* 3. Category Filter - ✅ Added X */}
                    {filters.category !== 'All Categories' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium border border-blue-200">
                            <Briefcase className="h-3 w-3" /> {filters.category}
                            <button 
                                onClick={() => handleFilterChange('category', 'All Categories')} 
                                className="ml-1 text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                            >
                                <X className="h-3 w-3"/>
                            </button>
                        </span>
                    )}

                    {/* 4. Trust Filter - ✅ Added X */}
                    {filters.min_trust !== 'any' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium border border-indigo-200">
                            <ThumbsUp className="h-3 w-3" /> {filters.min_trust}+ Endorsed
                            <button 
                                onClick={() => handleFilterChange('min_trust', 'any')} 
                                className="ml-1 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                            >
                                <X className="h-3 w-3"/>
                            </button>
                        </span>
                    )}

                    {/* 5. Rating Filter - ✅ Added X */}
                    {filters.rating !== 'all' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-medium border border-yellow-200">
                            <Star className="h-3 w-3" /> {filters.rating.replace('p', 'Stars')}
                            <button 
                                onClick={() => handleFilterChange('rating', 'all')} 
                                className="ml-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-200 rounded-full p-0.5 transition-colors"
                            >
                                <X className="h-3 w-3"/>
                            </button>
                        </span>
                    )}

                    {/* 6. Availability Status - ❌ NO X (As requested) */}
                    {filters.availability !== 'any' && (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${filters.availability === 'Available' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                            <Zap className="h-3 w-3" /> {filters.availability}
                        </span>
                    )}
                </div>
            )}
        </div>

        {/* RESULTS GRID */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-muted-foreground animate-pulse">Finding professionals...</p>
          </div>
        ) : providers.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 sm:p-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
              <MapPin className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">No Providers Found</h3>
            <p className="text-sm text-gray-500 mt-1 max-w-xs mx-auto">
                {filters.availability === 'Busy' ? "There are no providers marked as 'Busy' right now." : "Try adjusting your location or filters."}
            </p>
            <Button variant="outline" className="mt-4" onClick={clearFilters}>Clear All</Button>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-8">
                {providers.map((provider) => (
                <ProviderCard key={provider.id} provider={provider} />
                ))}
            </div>

            {/* ✅ PAGINATION CONTROLS */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pb-8">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className="h-9 w-9 p-0"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    <div className="text-sm font-medium text-gray-600 px-2">
                        Page {page} of {totalPages}
                    </div>

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                        className="h-9 w-9 p-0"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Providers() {
  return (
    <Suspense fallback={<div className="flex h-screen items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-primary" /></div>}>
      <ProvidersContent />
    </Suspense>
  );
}